// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { useColorScheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import GlobalStyles from '@mui/system/GlobalStyles'
import * as Sentry from '@sentry/react'
import { createFileRoute, Outlet, redirect, retainSearchParams, useNavigate } from '@tanstack/react-router'
import i18next from 'i18next'
import mobile from 'is-mobile'
import { HTTPError } from 'ky'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { authManager } from '../auth-manager.ts'
import { Box } from '../components/Box.tsx'
import { Button } from '../components/generic.tsx'
import { Notification } from '../components/Snackbar.tsx'
import { useDeviceStreams } from '../data/devices.ts'
import { type Impersonation, useImpersonation, useImpersonationActions } from '../data/impersonation.ts'
import { insightTokenQuery } from '../data/insight.ts'
import { useMeasurementStream } from '../data/liveMeasurements.ts'
import { customersQuery, productsQuery, subscriptionQuery } from '../data/service.ts'
import { currentUserData, hasRequiredRole, usePreferences, userPreferencesQuery } from '../data/userProfile.ts'
import { roleData } from '../data/users.ts'
import { AudibleAlerts } from '../features/alerts/AudibleAlerts.tsx'
import { IntercomMessenger } from '../Intercom.tsx'
import type { EN } from '../i18n/locales/EN.ts'
import { ids } from '../ids.ts'
import { panels, useMapState, useThemeMode } from '../states.ts'
import { UrlListSchema } from './-searchSchemas.ts'

const redirectOnSubscriptionError = (error: unknown): never => {
	Sentry.captureException(error)
	if (error instanceof HTTPError && error.response.status === 404) {
		throw redirect({ to: '/auth/error', search: { code: 'NO_SUBSCRIPTION' } })
	}
	throw redirect({ to: '/auth/error' })
}

export const Route = createFileRoute('/_app')({
	validateSearch: z.object({
		locationId: z.coerce.string().optional(),
		coordinates: z.object({ lat: z.number(), lon: z.number() }).optional(),
		layerGroups: UrlListSchema,
		layers: UrlListSchema,
		compareIds: UrlListSchema,
		panel: z.enum(panels).optional().catch(undefined),
		impersonation: z.string().optional(), // target subscription id, use the router context object to access impersonation
	}),
	search: {
		middlewares: [retainSearchParams(['impersonation'])],
	},
	component: Protected,
	beforeLoad: async ({ search, matches, context: { queryClient, ldClient } }) => {
		if (!authManager.isAuthenticated()) {
			if (authManager.getAuthError()) {
				throw redirect({ to: '/auth/error' })
			}
			throw redirect({ to: '/login', search: { returnTo: location.pathname + location.search + location.hash } })
		}

		const isMobileSite = matches.some((m) => m.routeId.startsWith('/_app/m/'))
		if (isMobileSite && !mobile()) throw redirect({ to: '/' })
		if (!isMobileSite && mobile()) throw redirect({ to: '/m' })

		await queryClient.ensureQueryData(subscriptionQuery(undefined)).catch(redirectOnSubscriptionError)

		const [currentUser, roles] = await Promise.all([currentUserData.ensure(), roleData.ensure()])
		const isAdmin = hasRequiredRole(currentUser.role, 'ADMIN')
		const isLimitedAdmin = currentUser.role.name === 'LIMITED_ADMIN'
		const isInternalAdmin = hasRequiredRole(currentUser.role, ['SUPER_ADMIN', 'INTERNAL_READ_ONLY'])

		if (!isInternalAdmin && search.impersonation) {
			throw redirect({ search: { ...search, impersonation: undefined } })
		}

		let impersonation: Impersonation | undefined
		if (isInternalAdmin && search.impersonation) {
			const [products, customers] = await Promise.all([
				queryClient.ensureQueryData(productsQuery),
				queryClient.ensureQueryData(customersQuery),
			])
			const product = products.find((p) => p.id === search.impersonation)
			if (product) {
				const customer = customers.find((c) => c.id === product.customerId)
				impersonation = {
					name: `${customer?.name} (${product.name})`,
					partyAccountId: product.partyAccountId,
					subscriptionId: product.id,
					currentLoggedinUserId: currentUser.id,
				}
			} else {
				throw redirect({ search: { ...search, impersonation: undefined } })
			}
		}

		const sub = await queryClient.ensureQueryData(subscriptionQuery(impersonation)).catch(redirectOnSubscriptionError)

		const identityKey = impersonation?.partyAccountId ?? currentUser.partyAccounts[0]
		if (!ldClient) {
			console.warn('LD Client is not available in router context, skipping identification')
		} else if (identityKey && ldClient.getContext().key !== identityKey) {
			await ldClient.identify({ kind: 'party', key: identityKey }).catch((error) => {
				Sentry.captureException(error)
				console.warn('LaunchDarkly identify failed', error)
			})
		}

		return { impersonation, currentUser, roles, userRoleName: currentUser.role, isAdmin, isLimitedAdmin, isInternalAdmin, sub }
	},
	loaderDeps: ({ search: { impersonation } }) => ({ impersonation }),
	loader: async ({ context: { queryClient, currentUser, sub } }) => {
		const [preferences] = await Promise.all([
			queryClient.ensureQueryData(userPreferencesQuery),
			queryClient.ensureQueryData(insightTokenQuery),
		])
		Sentry.setUser({ id: currentUser.id })

		if (globalThis.heap && globalThis.heap.getIdentity() !== currentUser.id) {
			globalThis.heap.identify(currentUser.id)
			globalThis.heap.addUserProperties({
				plan: sub.name,
				email: currentUser.email.email,
				customer: sub?.customer?.name || 'Unknown Customer',
			})
		}
		if (!i18next.isInitialized) {
			await i18next.init(
				{
					fallbackLng: 'EN',
					lng: preferences.language,
					partialBundledLanguages: true,
					resources: {},
					ns: ['translation', 'measurementTypes'] satisfies (keyof typeof EN)[],
					interpolation: {
						escapeValue: false,
					},
				},
				(err) => {
					if (err) console.warn('Initializing localization failed')
				},
			)
		}
	},
	notFoundComponent: NotFoundComponent,
})

function NotFoundComponent() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	useEffect(() => {
		enqueueSnackbar({ message: t(($) => $.snackbar.routeNotFound), variant: 'error' })
		navigate({ to: '/' }).catch(console.error)
	}, [navigate, t])
	return null
}

function Protected() {
	useDeviceStreams()
	useMeasurementStream()
	const { data: preferences } = usePreferences()

	useEffect(() => {
		i18next.changeLanguage(preferences.language).catch(console.error)
	}, [preferences.language])

	return (
		<>
			{!mobile() && (
				<GlobalStyles
					styles={{
						'.mapboxgl-ctrl-bottom, .mapboxgl-ctrl-bottom-left, .mapboxgl-ctrl-bottom-right, .mapboxgl-ctrl-left, .mapboxgl-ctrl-right, .mapboxgl-ctrl-top, .mapboxgl-ctrl-top-left, .mapboxgl-ctrl-top-right':
							{ zIndex: 'unset' },
						'[mapboxgl-children], .mapboxgl-control-container, #root > :not(.mapboxgl-map)': {
							'@media screen and (max-width: 1920px)': { zoom: 1 },
							'@media screen and (max-width: 1600px)': { zoom: 0.9 },
							'@media screen and (max-width: 1200px)': { zoom: 0.85 },
							'@media screen and (max-width: 960px)': { zoom: 0.8 },
							'@media screen and (max-width: 720px)': { zoom: 0.7 },
						},
						'.rdg, .no-zoom, .mapboxgl-map:not(#root > *), :is([mapboxgl-children], .mapboxgl-control-container, #root > :not(.mapboxgl-map)) .MuiTabs-indicator:not(:is(.rdg, .no-zoom) *)':
							{
								'@media screen and (max-width: 1920px)': { zoom: 1 },
								'@media screen and (max-width: 1600px)': { zoom: 1 / 0.9 },
								'@media screen and (max-width: 1200px)': { zoom: 1 / 0.85 },
								'@media screen and (max-width: 960px)': { zoom: 1 / 0.8 },
								'@media screen and (max-width: 720px)': { zoom: 1 / 0.7 },
							},
					}}
				/>
			)}
			<ThemeModeHandler />
			<SnackbarProvider
				maxSnack={3}
				preventDuplicate
				Components={{ success: Notification, info: Notification, warning: Notification, error: Notification }}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<ImpersonationFrame />
				<AudibleAlerts />
				<IntercomMessenger />
				<Outlet />
				{/* <TanStackRouterDevtools position='top-left' /> */}
				{/* <ReactQueryDevtools buttonPosition='top-left' /> */}
			</SnackbarProvider>
		</>
	)
}

const ThemeModeHandler = () => {
	const { setColorScheme } = useColorScheme()
	const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
	const { data: preferences } = usePreferences()
	const currentMode = useThemeMode((s) => s.mode)
	const setThemeMode = useThemeMode((s) => s.setMode)
	const setMapStyle = useMapState((s) => s.setMapStyle)

	useEffect(() => {
		setColorScheme(currentMode)
	}, [setColorScheme, currentMode])

	useEffect(() => {
		const mode = ({ LIGHT: 'light', DARK: 'dark', SYSTEM: 'system' } as const)[preferences.theme]
		const systemTheme = prefersDarkMode ? 'dark' : 'light'
		const lightOrDark = mode === 'system' ? systemTheme : mode
		setThemeMode(lightOrDark)
		setMapStyle(lightOrDark)
		setColorScheme(lightOrDark)
	}, [preferences, setColorScheme, prefersDarkMode, setThemeMode, setMapStyle])

	return null
}

const ImpersonationFrame = () => {
	const impersonation = useImpersonation()
	const { stopImpersonating } = useImpersonationActions()
	const { t } = useTranslation()
	if (!impersonation) return null

	return (
		<Box
			full
			sx={{ position: 'absolute', zIndex: 9999, border: 8, borderColor: (t) => t.palette.impersonation.main, pointerEvents: 'none' }}
		>
			<Box
				flex
				center
				sx={{
					position: 'absolute',
					bottom: -4,
					left: '50%',
					transform: 'translateX(-50%)',
					bgcolor: (t) => t.palette.impersonation.main,
					color: (t) => t.palette.impersonation.contrastText,
					radius: '8px 8px 0 0',
					px: 2,
					pt: 0.5,
					gap: 1,
					pointerEvents: 'initial',
				}}
			>
				{t(($) => $.impersonation.impersonatingAs, { name: impersonation.name })}
				<Button data-ta={ids.smp.impersonate.stopBtnBottom} onClick={stopImpersonating} color='secondary' sx={{ height: 20 }}>
					{t(($) => $.impersonation.stop)}
				</Button>
			</Box>
		</Box>
	)
}
